import i18n from 'i18n/config';
import { useCallback, useRef, useState } from 'react';

import debounce from 'lodash/debounce';

import { SelectorOption } from 'types';
import { CustomAsyncSelectOption } from '../types/CustomAsyncSelectOption';
import { getFilteredOptions } from '../utils/getFilteredOptions';
import { getOptionsData } from '../utils/getOptionsData';

type UseLoadOptionsProps = {
  defaultOptions: CustomAsyncSelectOption[];
  minimumInputLength: number;
  maximumInputLength: number;
  addOption: SelectorOption | undefined;
  noDataLabel: string;
  errorLabel: string;
  fetchData: (searchTerm: string) => Promise<CustomAsyncSelectOption[]>; // eslint-disable-line
};

export const useLoadOptions = ({
  minimumInputLength,
  maximumInputLength,
  defaultOptions,
  addOption,
  noDataLabel,
  errorLabel,
  fetchData,
}: UseLoadOptionsProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const lastTerm = useRef('');

  // Debounced fetch function
  const debouncedFetch = useRef(
    debounce(
      async (
        term: string,
        // eslint-disable-next-line no-unused-vars
        resolve: (options: CustomAsyncSelectOption[]) => void
      ) => {
        setIsLoading(true);
        lastTerm.current = term;

        try {
          const options = await getOptionsData({
            inputValue: term,
            minimumInputLength,
            addOption,
            noDataLabel,
            errorLabel,
            lastCallbackTerm: lastTerm,
            fetchData,
          });

          if (term === lastTerm.current) {
            resolve(options);
          } else {
            resolve([]);
          }
        } catch {
          resolve([]);
        } finally {
          setIsLoading(false);
        }
      },
      300
    )
  ).current;

  const loadOptions = useCallback(
    (inputValue: string) =>
      // eslint-disable-next-line no-async-promise-executor
      new Promise<CustomAsyncSelectOption[]>(async (resolve) => {
        if (inputValue.length > maximumInputLength) {
          resolve([]);
          return;
        }

        const filteredDefaultOptions = getFilteredOptions({
          inputValue,
          defaultOptions,
          addOption,
        });

        if (filteredDefaultOptions.length > 0) {
          resolve(filteredDefaultOptions);
          return;
        }

        if (inputValue.length < minimumInputLength) {
          resolve([
            {
              value: 'min-length',
              label: i18n.t('TypeAtLeastCharacters', {
                charactersCount: minimumInputLength,
              }),
              isDisabled: true,
            },
            ...(addOption ? [addOption] : []),
          ]);
          return;
        }

        debouncedFetch(inputValue, resolve);
      }),
    [defaultOptions, minimumInputLength, addOption, debouncedFetch]
  );

  return { actions: { loadOptions }, isLoading };
};
